import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/Button';
import { Textarea } from './ui/Textarea';
import { 
  ExternalLink, 
  Minimize2, 
  Maximize2, 
  X, 
  Save, 
  Eye, 
  Type, 
  Wand2,
  AlertCircle,
  Monitor,
  PictureInPicture,
  RotateCcw,
  Loader2,
  Undo,
  Redo
} from 'lucide-react';
import { useTranslation } from '../lib/i18n';

interface DetachableEditorProps {
  title: string;
  content: string;
  onContentChange: (content: string) => void;
  onTitleChange: (title: string) => void;
  onSave: () => void;
  onFormat: () => void;
  onImprove: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isSaving: boolean;
  isFormatting: boolean;
  isImproving: boolean;
  aiError: string | null;
  onClearError: () => void;
  isLoading: boolean;
  canUseAIFeature?: (level: 'basic' | 'advanced' | 'premium') => boolean;
}

const DetachableEditor: React.FC<DetachableEditorProps> = ({
  title,
  content,
  onContentChange,
  onTitleChange,
  onSave,
  onFormat,
  onImprove,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isSaving,
  isFormatting,
  isImproving,
  aiError,
  onClearError,
  isLoading,
  canUseAIFeature
}) => {
  const { t } = useTranslation();
  const [isDetached, setIsDetached] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 900, height: 700 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const detachedWindowRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<{ start: number; end: number } | null>(null);
  const previousContentLengthRef = useRef(content.length);
  const lastUserChangeTimeRef = useRef<number>(0);

  // Handle content change while preserving cursor position
  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;

    // Store cursor position before state update
    cursorPositionRef.current = {
      start: textarea.selectionStart,
      end: textarea.selectionEnd
    };

    // Mark the time of last user change
    lastUserChangeTimeRef.current = Date.now();

    // Call parent's onChange handler
    onContentChange(textarea.value);
  }, [onContentChange]);

  // Restore cursor position after content updates
  useEffect(() => {
    const textarea = document.getElementById('main-document-editor') as HTMLTextAreaElement;

    if (!textarea || !cursorPositionRef.current) {
      previousContentLengthRef.current = content.length;
      return;
    }

    const { start, end } = cursorPositionRef.current;
    const contentLengthChange = Math.abs(content.length - previousContentLengthRef.current);
    const timeSinceLastUserChange = Date.now() - lastUserChangeTimeRef.current;

    // Only restore cursor if:
    // 1. The content length change is small (< 100 chars) - indicates typing, not bulk operation
    // 2. The change happened very recently (< 100ms) - indicates user typing
    // 3. The cursor position is still valid
    const shouldRestoreCursor =
      contentLengthChange < 100 &&
      timeSinceLastUserChange < 100 &&
      start <= content.length &&
      end <= content.length;

    if (shouldRestoreCursor) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        try {
          if (textarea === document.activeElement) {
            textarea.setSelectionRange(start, end);
          }
        } catch (e) {
          // Ignore errors if textarea is not focusable
        }
      });
    }

    // Update the previous content length
    previousContentLengthRef.current = content.length;
  }, [content]);

  // Handle window resize to keep detached editor in bounds
  useEffect(() => {
    const handleWindowResize = () => {
      if (isDetached) {
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;
        setPosition(prev => ({
          x: Math.min(Math.max(0, prev.x), maxX),
          y: Math.min(Math.max(0, prev.y), maxY)
        }));
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isDetached, size]);

  // Handle dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isDetached || isMinimized) return;
    
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  }, [isDetached, isMinimized, position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;
    
    const maxX = window.innerWidth - size.width;
    const maxY = window.innerHeight - size.height;
    
    setPosition({
      x: Math.min(Math.max(0, newX), maxX),
      y: Math.min(Math.max(0, newY), maxY)
    });
  }, [isDragging, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle resizing
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!isDetached || isMinimized) return;
    
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
    e.preventDefault();
    e.stopPropagation();
  }, [isDetached, isMinimized, size]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;
    
    const newWidth = Math.min(Math.max(500, resizeStartRef.current.width + deltaX), window.innerWidth - position.x);
    const newHeight = Math.min(Math.max(400, resizeStartRef.current.height + deltaY), window.innerHeight - position.y);
    
    setSize({ width: newWidth, height: newHeight });
  }, [isResizing, position]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Event listeners for dragging and resizing
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp, handleResizeMove, handleResizeEnd]);

  const handleDetach = () => {
    setIsDetached(true);
    setIsMinimized(false);
    // Center the detached window
    setPosition({
      x: (window.innerWidth - size.width) / 2,
      y: (window.innerHeight - size.height) / 2
    });
  };

  const handleReattach = () => {
    setIsDetached(false);
    setIsMinimized(false);
    setShowPreview(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const renderPreview = () => {
    const htmlContent = content
      .replace(/═{63}/g, '<hr style="border: 3px solid #3b82f6; margin: 30px 0; border-radius: 2px; box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);">')
      .replace(/─{63}/g, '<hr style="border: 1px solid #6b7280; margin: 20px 0; opacity: 0.7;">')
      .replace(/^([A-Z\s:]+)$/gm, '<h2 style="font-weight: 900; font-size: 28px; margin: 30px 0; text-align: center; color: #1f2937; text-transform: uppercase; letter-spacing: 2px; text-shadow: 2px 2px 4px rgba(0,0,0,0.1);">$1</h2>')
      .replace(/^([A-Z][A-Z\s]+:)/gm, '<h3 style="font-weight: 800; font-size: 22px; margin: 25px 0; color: #374151; text-transform: uppercase; letter-spacing: 1px;">$1</h3>')
      .replace(/^•\s(.+)/gm, '<li style="margin: 10px 0; font-weight: 600; color: #4b5563; padding-left: 8px; border-left: 3px solid #3b82f6;">$1</li>')
      .replace(/^\d+\.\s(.+)/gm, '<li style="margin: 10px 0; list-style-type: decimal; font-weight: 600; color: #4b5563; padding-left: 8px;">$1</li>')
      .replace(/\n/g, '<br>');

    return (
      <div 
        className="h-full overflow-y-auto p-8 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          lineHeight: '1.9',
          fontSize: '17px'
        }}
      >
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          marginBottom: '40px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1f2937, #3b82f6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textTransform: 'uppercase',
          letterSpacing: '3px',
          textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
        }}>
          {title}
        </h1>
        <div 
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{ 
            color: '#374151',
            textShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}
          className="dark:text-gray-200"
        />
      </div>
    );
  };

  // Embedded editor (when not detached)
  if (!isDetached) {
    return (
      <div className="h-full flex flex-col">
        {/* Detach Button */}
        <div className="mb-4 flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg border border-blue-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <Button
              onClick={handleDetach}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2 font-bold border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
            >
              <ExternalLink className="w-4 h-4" />
              <span>{t('detach.detachEditor')}</span>
            </Button>
            <Button
              onClick={() => setShowPreview(!showPreview)}
              variant={showPreview ? "default" : "outline"}
              size="sm"
              className="flex items-center space-x-2 font-bold"
            >
              <Eye className="w-4 h-4" />
              <span>{t('detach.preview')}</span>
            </Button>
          </div>
        </div>

        {aiError && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 rounded-lg flex items-center border border-red-300 dark:border-red-700 shadow-sm">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="font-medium">{aiError}</span>
            <button
              onClick={onClearError}
              className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200 p-1 rounded hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {showPreview ? (
          <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <h3 className="text-lg font-black mb-3 flex items-center text-gray-800 dark:text-gray-200">
                <Type className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                {t('detach.editor')}
              </h3>
              <Textarea
                id="main-document-editor"
                value={content}
                onChange={handleContentChange}
                placeholder={t('detach.startTypingDoc')}
                className="flex-1 resize-none document-editor-textarea shadow-lg"
              />
            </div>
            <div className="flex flex-col">
              <h3 className="text-lg font-black mb-3 flex items-center text-gray-800 dark:text-gray-200">
                <Eye className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
                {t('detach.livePreview')}
              </h3>
              <div className="flex-1 border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden shadow-lg">
                {renderPreview()}
              </div>
            </div>
          </div>
        ) : (
          <Textarea
            id="main-document-editor"
            value={content}
            onChange={handleContentChange}
            placeholder={t('detach.placeholderTips')}
            className="flex-1 resize-none document-editor-textarea shadow-lg"
          />
        )}
      </div>
    );
  }

  // Detached editor window
  return (
    <>
      {/* Reattach Button (shown in main interface when detached) */}
      <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg shadow-inner">
        <div className="text-center p-8">
          <div className="relative mb-6">
            <Monitor className="w-20 h-20 mx-auto text-blue-500 dark:text-blue-400" />
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
          <h3 className="text-2xl font-black text-gray-800 dark:text-gray-200 mb-3">
            {t('detach.editorDetached')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
            {t('detach.detachedDesc')}
          </p>
          <Button
            onClick={handleReattach}
            className="flex items-center space-x-2 font-bold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg px-6 py-3"
          >
            <RotateCcw className="w-5 h-5" />
            <span>{t('detach.reattachEditor')}</span>
          </Button>
        </div>
      </div>

      {/* Detached Editor Window */}
      <div
        ref={detachedWindowRef}
        className={`fixed z-50 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl shadow-2xl transition-all duration-300 ${
          isMinimized ? 'h-14' : ''
        }`}
        style={{
          left: position.x,
          top: position.y,
          width: isMinimized ? 350 : size.width,
          height: isMinimized ? 56 : size.height,
          zIndex: 1000,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* Window Header */}
        <div
          className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white rounded-t-xl cursor-move select-none shadow-lg"
          onMouseDown={handleMouseDown}
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6, #3b82f6)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="flex items-center space-x-3">
            <PictureInPicture className="w-5 h-5" />
            <span className="font-black text-sm tracking-wide">{t('detach.detachedLabel')}</span>
            {isMinimized && (
              <span className="text-xs opacity-90 font-medium">- {title.substring(0, 30)}{title.length > 30 ? '...' : ''}</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleMinimize}
              className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
            >
              {isMinimized ? (
                <Maximize2 className="w-4 h-4" />
              ) : (
                <Minimize2 className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={handleReattach}
              className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Window Content */}
        {!isMinimized && (
          <div className="flex flex-col h-full bg-white dark:bg-gray-800">
            {/* Title Input */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <input
                type="text"
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder={t('detach.titlePlaceholder')}
                className="w-full text-xl font-black bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={isLoading}
                style={{
                  textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                }}
              />
            </div>

            {/* Toolbar */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700">
              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => setShowPreview(!showPreview)}
                  variant={showPreview ? "default" : "outline"}
                  size="sm"
                  className="font-bold"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {t('detach.preview')}
                </Button>
              </div>
            </div>

            {aiError && (
              <div className="mx-4 mt-4 p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 rounded-lg flex items-center border border-red-300 dark:border-red-700">
                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="font-medium">{aiError}</span>
                <button
                  onClick={onClearError}
                  className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200 p-1 rounded hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Editor Content */}
            <div className="flex-1 p-4 overflow-hidden">
              {showPreview ? (
                <div className="h-full grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <h4 className="text-sm font-black mb-3 text-gray-800 dark:text-gray-200 flex items-center">
                      <Type className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                      {t('detach.editor')}
                    </h4>
                    <Textarea
                      id="main-document-editor"
                      value={content}
                      onChange={handleContentChange}
                      placeholder={t('detach.startTyping')}
                      className="flex-1 resize-none text-sm document-editor-textarea"
                    />
                  </div>
                  <div className="flex flex-col">
                    <h4 className="text-sm font-black mb-3 text-gray-800 dark:text-gray-200 flex items-center">
                      <Eye className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                      {t('detach.livePreview')}
                    </h4>
                    <div className="flex-1 border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden shadow-inner">
                      {renderPreview()}
                    </div>
                  </div>
                </div>
              ) : (
                <Textarea
                  id="main-document-editor"
                  value={content}
                  onChange={handleContentChange}
                  placeholder={t('detach.startTypingDoc')}
                  className="w-full h-full resize-none document-editor-textarea"
                />
              )}
            </div>

            {/* Resize Handle */}
            <div
              className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize opacity-60 hover:opacity-100 transition-opacity duration-200"
              onMouseDown={handleResizeStart}
              style={{
                background: 'linear-gradient(135deg, transparent 40%, #6b7280 40%, #6b7280 60%, transparent 60%)',
                borderBottomRightRadius: '0.75rem'
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DetachableEditor;